import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { type QueryState, wrapQuery, narrowContext } from "../queryState";
import type { ProjectDashboardVM } from "../types/project";
import {
  useIsInsideSubscriptionScope,
  useSubscriptionActive,
} from "../../components/layout/SubscriptionScope";

type ProjectDashboardRaw = FunctionReturnType<
  typeof api.composites.projectDashboard.projectDashboard
>;

const ProjectDashboardCtx = createContext<QueryState<ProjectDashboardVM>>(
  { status: "loading" }
);

export function ProjectDashboardProvider({
  projectId, children,
}: { projectId: Id<"projects">; children: ReactNode }) {
  const active = useSubscriptionActive();

  // v6-E (T317, FR-032): warn once in development when the provider is
  // mounted outside any <SubscriptionScope>. This indicates a missing
  // wrapper around the project routes and means the composite will never
  // receive "skip" on navigation away, leaking a subscription. In
  // production we stay silent to avoid noisy end-user consoles.
  const insideScope = useIsInsideSubscriptionScope();
  const warnedRef = useRef(false);
  useEffect(() => {
    if (insideScope || warnedRef.current) return;
    // `import.meta.env.DEV` is set to true by Vite in dev builds and
    // false in production builds. In test environments it may be
    // undefined — we only warn when it is explicitly true so the
    // warning is a development-only signal.
    const isDev = typeof import.meta !== "undefined"
      && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
    if (!isDev) return;
    warnedRef.current = true;
    // eslint-disable-next-line no-console
    console.warn(
      "[ProjectDashboardProvider] rendered outside <SubscriptionScope>. " +
      "The projectDashboard subscription will never pause on navigation. " +
      "Wrap project-level routes in <SubscriptionScope active={...}> " +
      "(see specs/architecture.md §4.6).",
    );
  }, [insideScope]);

  const raw = useQuery(
    api.composites.projectDashboard.projectDashboard,
    active ? { projectId } : "skip"
  );
  const state = wrapQuery(raw, toProjectDashboardVM);
  return (
    <ProjectDashboardCtx.Provider value={state}>
      {children}
    </ProjectDashboardCtx.Provider>
  );
}

function toProjectDashboardVM(raw: ProjectDashboardRaw): ProjectDashboardVM {
  return { project: raw.project, tasks: raw.tasks, waves: raw.waves,
    execByTask: raw.execByTask, unblockedTaskIds: raw.unblockedTaskIds,
    statusSummary: raw.statusSummary, aggregates: raw.aggregates };
}

// 7 selectors using narrowContext (zero `as` casts).
//
// v6-E change (T308 — FR-016, Stack Review R3):
// All 7 selectors stabilize their returned slice with useMemo + JSON.stringify
// guards. Previously only useUnblockedTaskIds was memoized; the others returned
// new references on every composite push (one push every few seconds during
// active execution), forcing every context consumer to re-render even when its
// specific slice had not changed.
//
// Pattern:
//   const ctx = useContext(ProjectDashboardCtx);
//   const slice = ctx.status === "ready" ? ctx.data.<field> : undefined;
//   const stableSlice = useMemo(
//     () => slice,
//     [slice ? JSON.stringify(slice) : ""]
//   );
//   return narrowContext(ctx, () => stableSlice!);
//
// JSON.stringify is acceptable here because view-model slices are small
// (typically <1KB) and the cost is negligible vs a full component tree
// re-render. See specs/architecture.md Section 4.7.
export function useProjectDashboard(): QueryState<ProjectDashboardVM> {
  return useContext(ProjectDashboardCtx);
}
export function useStatusSummary() {
  const ctx = useContext(ProjectDashboardCtx);
  const summary = ctx.status === "ready" ? ctx.data.statusSummary : undefined;
  const stableSummary = useMemo(
    () => summary,
    [summary ? JSON.stringify(summary) : ""]
  );
  return narrowContext(ctx, () => stableSummary!);
}
export function useTaskNodes() {
  const ctx = useContext(ProjectDashboardCtx);
  const tasks = ctx.status === "ready" ? ctx.data.tasks : undefined;
  const stableTasks = useMemo(
    () => tasks,
    [tasks ? JSON.stringify(tasks) : ""]
  );
  return narrowContext(ctx, () => stableTasks!);
}
export function useExecByTask() {
  const ctx = useContext(ProjectDashboardCtx);
  const execByTask = ctx.status === "ready" ? ctx.data.execByTask : undefined;
  const stableExecByTask = useMemo(
    () => execByTask,
    [execByTask ? JSON.stringify(execByTask) : ""]
  );
  return narrowContext(ctx, () => stableExecByTask!);
}
export function useUnblockedTaskIds() {
  const ctx = useContext(ProjectDashboardCtx);
  const ids = ctx.status === "ready" ? ctx.data.unblockedTaskIds : undefined;
  const idSet = useMemo(
    () => ids ? new Set(ids) : new Set<string>(),
    [ids ? JSON.stringify(ids) : ""]
  );
  return narrowContext(ctx, () => idSet);
}
export function useWaves() {
  const ctx = useContext(ProjectDashboardCtx);
  const waves = ctx.status === "ready" ? ctx.data.waves : undefined;
  const stableWaves = useMemo(
    () => waves,
    [waves ? JSON.stringify(waves) : ""]
  );
  return narrowContext(ctx, () => stableWaves!);
}
export function useAggregates() {
  const ctx = useContext(ProjectDashboardCtx);
  const aggregates = ctx.status === "ready" ? ctx.data.aggregates : undefined;
  // aggregates may legitimately be `null` (no executions yet). Stringify the
  // JSON representation so the dep key is stable across re-renders with the
  // same null value, but changes when the aggregate fields change.
  const stableAggregates = useMemo(
    () => aggregates,
    [aggregates !== undefined ? JSON.stringify(aggregates) : ""]
  );
  // aggregates is `AggregatesVM | null` — preserve null semantics (do not
  // non-null-assert, unlike the other selectors where the slice is always
  // defined when ready).
  return narrowContext(ctx, () => stableAggregates ?? null);
}
export function useProjectMeta() {
  const ctx = useContext(ProjectDashboardCtx);
  const project = ctx.status === "ready" ? ctx.data.project : undefined;
  const stableProject = useMemo(
    () => project,
    [project ? JSON.stringify(project) : ""]
  );
  return narrowContext(ctx, () => stableProject!);
}

// Raw access for notification diff-tracking (returns undefined during loading)
export function useRawTaskStatuses() {
  const ctx = useContext(ProjectDashboardCtx);
  if (ctx.status !== "ready") return undefined;
  return ctx.data.tasks.map(t => ({ taskId: t.taskId, status: t.status, subject: t.subject }));
}
